import Tool, {
  type ToolArgs,
  type ToolArgsWithController,
} from '../../../tools/Tool';
import { Editor, Transforms, Node, type PathRef, Range, Path } from 'slate';
import TableUtils from '../TableUtils';
import { getTableStructureFromSelection } from '../TableStructure';

interface SortedRow {
  content: string;
  rowPath: PathRef;
}

type Direction = 'asc' | 'desc';

const getSelectedColumnIndices = (
  editor: Editor,
  tablePath: number[]
): [number, number] | undefined => {
  if (!editor.selection) return;

  const [start, end] = Range.edges(editor.selection);
  const commonPath = Path.common(tablePath, start.path);

  const startCellIndex = start.path[commonPath.length + 1];
  const endCellIndex = end.path[commonPath.length + 1];

  return [startCellIndex, endCellIndex];
};

const selectionInSameColumn = (
  editor: Editor,
  tablePath: number[]
): boolean => {
  if (!editor.selection) return false;

  const indices = getSelectedColumnIndices(editor, tablePath);
  if (!indices) return false;

  return indices[0] === indices[1];
};

const sortingEnabled = (editor: Editor): boolean => {
  // Only enabled if we're inside a table
  const tablePath = TableUtils.currentTablePath(editor);
  if (!tablePath) return false;

  const tableStructure = getTableStructureFromSelection(editor);
  if (!tableStructure) return false;

  const singleColumnSelected = selectionInSameColumn(editor, tablePath);
  if (!singleColumnSelected) return false;

  // Only enabled if there are no merged cells in table. It's possible to
  // sort with merged cells, but this might come in a future enhancement.
  return !tableStructure.hasMergedCellsInTable();
};

const featureFlagEnabled = () => {
  return Boolean(window.featureFlags?.CREATE_2861_sort_tables_notes);
};

class ToolTableSort extends Tool {
  static direction: Direction;

  static action({ editor }: ToolArgsWithController): void {
    if (!this.direction) {
      throw new Error(
        'You must define a static "direction" property in the subclass.'
      );
    }

    if (!editor.selection) return;

    // Save the current selection to restore it later
    const [anchor, focus] = Range.edges(editor.selection);

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const tableEntry = TableUtils.currentTable(editor);
    if (!tableEntry) return;

    const [tableElement, tablePath] = tableEntry;

    const indices = getSelectedColumnIndices(editor, tablePath);
    if (!indices) return;

    const [columnIndex, endColumnIndex] = indices;
    if (columnIndex !== endColumnIndex) return;

    // Check if table has header row to skip it during sorting
    const hasHeaderRow = tableElement.table_headerRow;
    const startRowIndex = hasHeaderRow ? 1 : 0;

    // Get all rows with their content for sorting
    const rowsToSort: SortedRow[] = [];

    for (let i = startRowIndex; i < tableStructure.height; i++) {
      const cell = tableStructure.cell(columnIndex, i);
      if (!cell) continue;

      // Get the text content of the cell for sorting
      const cellPath = [...tablePath, ...cell.relativePath];
      const cellNode = Node.get(editor, cellPath);
      const cellContent = Node.string(cellNode).toLowerCase().trim();

      rowsToSort.push({
        content: cellContent,
        rowPath: Editor.pathRef(editor, [...tablePath, i]),
      });
    }

    // Sort rows by content
    rowsToSort.sort((a, b) => {
      // We want the array in reverse order so we can move the last row first
      const [left, right] = this.direction === 'desc' ? [a, b] : [b, a];

      // Try to parse as numbers first
      const leftNum = parseFloat(left.content);
      const rightNum = parseFloat(right.content);

      if (!isNaN(leftNum) && !isNaN(rightNum)) {
        return leftNum - rightNum;
      }

      // Otherwise sort alphabetically
      return left.content.localeCompare(right.content);
    });

    // Reorder the rows using Transforms.moveNodes. We're using a stored PathRef
    // to ensure we have the correct path even as the document changes so we can
    // move rows in one pass.
    Editor.withoutNormalizing(editor, () => {
      rowsToSort.forEach((row) => {
        const path = row.rowPath.unref();
        if (!path) return;

        Transforms.moveNodes(editor, {
          at: path,
          to: [...path.slice(0, -1), hasHeaderRow ? 1 : 0],
        });
      });
    });

    // Restore the original selection so the user's selection doesn't jump
    // around after sorting.
    Transforms.select(editor, { anchor, focus });
  }

  static isEnabled({ editor }: ToolArgs): boolean {
    if (!featureFlagEnabled()) return false;

    return sortingEnabled(editor);
  }

  static available() {
    return featureFlagEnabled();
  }
}

export class ToolTableSortAsc extends ToolTableSort {
  static toolName = 'Sort A to Z / 0 to 9';
  static iconClass = 'fa-solid fa-arrow-down-short-wide';
  static direction: Direction = 'asc';
}

export class ToolTableSortDesc extends ToolTableSort {
  static toolName = 'Sort Z to A / 9 to 0';
  static iconClass = 'fa-solid fa-arrow-down-wide-short';
  static direction: Direction = 'desc';
}

export class ToolTableSortOptions extends Tool {
  static toolName = 'Table sort options';
  static iconClass = 'fa-regular fa-arrow-up-arrow-down';
  static submenuType = 'list-toggles' as const;

  static submenuTools = () => [[ToolTableSortAsc, ToolTableSortDesc]];

  static isEnabled({ editor }: ToolArgs): boolean {
    if (!featureFlagEnabled()) return false;

    return sortingEnabled(editor);
  }

  static available() {
    return featureFlagEnabled();
  }
}
